<template>
  <Modal
    class="scoped"
    full-height
    :modal-style="{ maxWidth: '960px' }"
    :title="title"
  >
    <div
      v-if="loadingBriefs"
      class="q-mt-md"
    >
      <QLinearProgress
        color="primary"
        size="10px"
        stripe
        :value="loadingProgress"
      />
      <div class="text-caption q-mt-xs text-center">
        Loading briefs: {{ Math.round(loadingProgress * 100) }}%
      </div>
    </div>
    <QForm
      v-else
      class="c-enable-briefs-details-step"
    >
      <FormStep :step="1">
        <div class="c-enable-briefs-details-step__section">
          <div class="c-enable-briefs-details-step__section-header">
            <div class="c-enable-briefs-details-step__section-header">
              Choose Users
            </div>
            <div class="c-enable-briefs-details-step__section-subheader">
              Select the user accounts where you want to
              {{ action.toLowerCase() }} briefs.
            </div>
          </div>
          <QExpansionItem
            :caption="usersSelectedLabel"
            class="text-primary"
            group="tables"
          >
            <SelectUsersTable
              v-if="users"
              v-model:filters="userFilters"
              v-model:pagination="usersPagination"
              :loading="loadingUsers"
              :users="users"
              @request="debouncedSearch"
              @update="updateSelectedUsers"
              @update:filters="debouncedSearch"
            />
          </QExpansionItem>
        </div>
      </FormStep>
      <FormStep
        is-last-step
        :step="2"
      >
        <div class="c-enable-briefs-details-step__section">
          <div class="c-enable-briefs-details-step__section-header">
            Choose Briefs to {{ action }}
          </div>
          <div class="c-enable-briefs-details-step__section-subheader">
            {{ action }}
            multiple briefs or briefs sets for the above users.
          </div>

          <template v-if="selectedUsers.length === 0">
            <p class="text-red">Please select users first</p>
          </template>

          <QExpansionItem
            v-else
            :caption="briefsSelectedLabel"
            class="text-primary"
            group="tables"
          >
            <template v-if="action === 'Assign'">
              <QRadio
                v-model="briefSelectionMethod"
                label="Assign Brief Sets"
                val="assign_brief_sets"
              />
              <QRadio
                v-model="briefSelectionMethod"
                label="Assign individual Briefs"
                val="assign_individual_briefs"
                @update:model-value="onBriefSelectionMethodChange"
              />
            </template>

            <template v-else>
              <QRadio
                v-model="briefSelectionMethod"
                label="Unassign Individual Briefs"
                val="unassign_individual_briefs"
                @update:model-value="onBriefSelectionMethodChange"
              >
                <StreemTooltip>
                  Uncheck the briefs you would like to unassign from the above
                  users
                </StreemTooltip>
              </QRadio>
              <QRadio
                v-model="briefSelectionMethod"
                label="Unassign Brief Sets"
                val="unassign_brief_sets"
              />
              <QRadio
                v-model="briefSelectionMethod"
                label="Unassign all Briefs and Brief Sets"
                val="unassign_all_briefs_and_brief_sets"
              />
            </template>

            <template v-if="briefSelectionMethod === 'assign_brief_sets'">
              <QSpinner
                v-if="loadingBriefs"
                class="full-width q-mt-sm"
                :size="30"
              />
              <template v-else>
                <BriefSetSelector
                  v-model="selectedBriefSetIds"
                  :bottom-slots="false"
                  class="q-mt-sm"
                  label="Brief Sets"
                  multiple
                  :organisation-id="organisationId"
                  use-chips
                />
                <br />
                <SelectList
                  v-if="selectedBriefs.length"
                  v-model="primaryBrief"
                  class="q-pb-md"
                  label="Set Primary Brief"
                  :options="primaryBriefOptions"
                />
              </template>
            </template>

            <SelectBriefsTable
              v-if="briefSelectionMethod === 'assign_individual_briefs'"
              :briefs="briefs"
              :initial-selected="selectedBriefs"
              :loading="loadingBriefs"
              @update="updateSelectedBriefs"
            />

            <SelectBriefsTable
              v-if="briefSelectionMethod === 'unassign_individual_briefs'"
              :briefs="allIndividualBriefsForSelectedUsers"
              :filter-by-users="selectedUserIds"
              :initial-selected="selectedBriefs"
              :loading="loadingBriefs"
              reverse-selection
              @update="updateSelectedBriefs"
            />

            <template v-if="briefSelectionMethod === 'unassign_brief_sets'">
              <QSpinner
                v-if="loadingBriefs"
                class="full-width q-mt-sm"
                :size="30"
              />
              <template v-else>
                <BriefSetSelector
                  v-model="selectedBriefSetIds"
                  :bottom-slots="false"
                  class="q-mt-sm"
                  label="Brief Sets"
                  multiple
                  :organisation-id="organisationId"
                  use-chips
                  :user-ids="selectedUserIds"
                />
                <br />
                <p class="text-red">
                  <QIcon name="info" />
                  <span>
                    Briefs Sets that are assigned via a team will remain.
                  </span>
                </p>
              </template>
            </template>
            <template
              v-else-if="
                briefSelectionMethod === 'unassign_all_briefs_and_brief_sets'
              "
            >
              <p class="text-red">
                ! This will unassign all individual Briefs and Brief Sets
                assigned directly to the user(s).
                <br />
                Briefs Sets that are assigned via a team will remain.
              </p>
            </template>
          </QExpansionItem>
        </div>
      </FormStep>
    </QForm>

    <template #buttons>
      <BaseButton
        color="primary"
        :disabled="buttonDisabled"
        :label="buttonLabel"
        new-design
        @click="buttonClick"
      />
    </template>
  </Modal>
</template>

<script>
import { debounce, intersection } from "lodash-es";

import BaseButton from "shared/components/base/BaseButton.vue";
import Modal from "shared/components/core/modals/Modal.vue";
import FormStep from "shared/components/FormStep.vue";
import { pluralize } from "shared/helpers/string";
import { User } from "shared/resources";
import BriefSet from "shared/resources/BriefSet";
import BriefSetsStream from "shared/resources/BriefSetsStream";

import SelectBriefsTable from "src/components/briefs/SelectBriefsTable.vue";
import BriefSetSelector from "src/components/briefSets/BriefSetSelector.vue";
import SelectList from "src/components/common/SelectList.vue";
import SelectUsersTable from "src/components/users/SelectUsersTable.vue";

export default {
  name: "BriefUsersBulkSelectionModal",
  components: {
    Modal,
    BaseButton,
    FormStep,
    BriefSetSelector,
    SelectBriefsTable,
    SelectUsersTable,
    SelectList,
  },
  props: {
    organisationId: {
      type: [Number, String],
      required: true,
    },
    action: {
      type: String,
      required: true,
    },
  },
  emits: ["confirm", "close", "refresh"],
  data() {
    return {
      selectedBriefs: [],
      selectedUsers: [],
      primaryBrief: null,
      users: [],
      briefs: [],
      loadingUsers: false,
      loadingBriefs: false,
      briefSelectionMethod: "",
      selectedBriefSetIds: [],
      allIndividualBriefsForSelectedUsers: [],
      debouncedSearch: debounce(this.loadUsers, 500),
      usersPagination: {
        rowsPerPage: 50,
        page: 1,
        sortBy: "id",
        descending: true,
        rowsNumber: 0,
      },
      userFilters: {
        id: {
          value: "",
          operator: "eq",
        },
        firstName: {
          value: "",
          operator: "match",
        },
        lastName: {
          value: "",
          operator: "match",
        },
        email: {
          value: "",
          operator: "match",
        },
        teamName: {
          value: "",
          operator: "match",
        },
      },
      loadingProgress: 0,
    };
  },
  computed: {
    buttonLabel() {
      return String(this.action);
    },
    buttonDisabled() {
      return (
        this.selectedUsers.length === 0 ||
        !this.briefSelectionMethod ||
        (this.selectedBriefs.length === 0 &&
          ["assign_individual_briefs", "unassign_individual_briefs"].includes(
            this.briefSelectionMethod
          )) ||
        (this.selectedBriefSetIds.length === 0 &&
          ["assign_brief_sets", "unassign_brief_sets"].includes(
            this.briefSelectionMethod
          )) ||
        (this.briefSelectionMethod === "unassign_all_briefs" &&
          this.allIndividualBriefsForSelectedUsers.length === 0)
      );
    },
    title() {
      return `Bulk ${this.action} Briefs for Multiple Users`;
    },
    usersSelectedLabel() {
      return `${this.selectedUsers.length} selected`;
    },
    briefsSelectedLabel() {
      return `${this.selectedBriefs.length} briefs selected`;
    },
    primaryBriefOptions() {
      return this.selectedBriefs.map(({ id, label, group }) => ({
        id,
        label: `${label} (${group.label})`,
      }));
    },
    selectedUserIds() {
      return this.selectedUsers.map((user) => user.id);
    },
  },
  watch: {
    async selectedBriefSetIds() {
      if (
        (this.briefSelectionMethod === "assign_brief_sets" ||
          this.briefSelectionMethod === "unassign_brief_sets") &&
        this.selectedBriefSetIds
      ) {
        const briefSets = await this.loadBriefSetsStreams();

        const briefSetsStreams = briefSets
          .map((briefSet) => briefSet.briefSetsStreams)
          .flat();

        const briefSetStreamIds = new Set(
          briefSetsStreams.map((briefSetsStream) => briefSetsStream.streamId)
        );

        const primaryBrief = briefSetsStreams.find(
          (briefSetsStream) => briefSetsStream.primary
        );

        this.primaryBrief = primaryBrief ? primaryBrief.streamId : null;

        this.selectedBriefs = this.briefs.filter((brief) =>
          briefSetStreamIds.has(brief.id)
        );
      }
    },
  },
  mounted() {
    if (this.organisationId) {
      this.debouncedSearch();
    }
  },
  methods: {
    async onBriefSelectionMethodChange(newValue) {
      if (newValue === "unassign_individual_briefs") {
        await this.loadBriefs();
        this.setallIndividualBriefsForSelectedUsers();

        return;
      }

      if (newValue === "assign_individual_briefs") {
        await this.loadBriefs();
      }
    },
    buttonClick() {
      if (this.action === "Unassign") {
        this.openConfirmModal();
      } else {
        this.save();
      }
    },
    updateSelectedBriefs(briefs) {
      this.selectedBriefs = briefs;
    },
    updateSelectedUsers(users) {
      this.selectedUsers = users;
      this.setallIndividualBriefsForSelectedUsers();
    },
    setallIndividualBriefsForSelectedUsers() {
      const selectedUserIds = new Set(
        this.selectedUsers.map((user) => Number(user.id))
      );

      const briefSetIdsForSelectedUsers = this.selectedUsers
        .map((user) => user.briefSetIds)
        .flat();

      this.allIndividualBriefsForSelectedUsers = this.briefs.filter((brief) =>
        brief.users.some(
          (user) =>
            selectedUserIds.has(user.id) &&
            !intersection(briefSetIdsForSelectedUsers, brief.brief_set_ids)
              .length
        )
      );
    },
    openConfirmModal() {
      const object =
        this.briefSelectionMethod === "unassign_brief_sets"
          ? "brief set"
          : "brief";

      const userCounts = `${this.selectedUsers.length} ${pluralize(
        this.selectedUsers.length,
        "user"
      )}`;

      const message = `Are you sure you want to<br /> unassign selected ${object} <br /> from ${userCounts}?`;

      this.$dialog
        .confirm({
          title: "Are you sure?",
          message,
          theme: "danger",
          persistent: true,
          dialogMaxWidth: "280px",
        })
        .onOk(() => {
          this.save();
        });
    },
    async fetchBriefBatch(page, baseParams, pageSize = 100) {
      const params = {
        ...baseParams,
        page,
        page_size: pageSize,
      };

      const response = await this.$streemApiAdmin.get(
        `organisations/${this.organisationId}/briefs`,
        {
          params,
          timeout: 120000,
        }
      );

      return {
        briefs: response.data || [],
        totalCount: parseInt(response.headers["x-total-count"] || 0, 10),
      };
    },
    async loadBriefs() {
      try {
        const batchSize = 100;
        this.loadingBriefs = true;
        this.loadingProgress = 0;

        const baseParams = {};

        if (this.action === "Unassign") {
          baseParams.include_users = true;
          baseParams.user_ids = this.selectedUsers.map((user) => user.id);
        }

        const { briefs: firstPageBriefs, totalCount } =
          await this.fetchBriefBatch(1, baseParams, batchSize);

        if (totalCount === 0) {
          this.briefs = [];

          return;
        }

        const totalBatches = Math.ceil(totalCount / batchSize);
        const loadedBriefs = [...firstPageBriefs];

        if (totalBatches > 1) {
          for (let page = 2; page <= totalBatches; page += 1) {
            this.loadingProgress = page / totalBatches;

            try {
              // eslint-disable-next-line no-await-in-loop
              const { briefs: pageBriefs } = await this.fetchBriefBatch(
                page,
                baseParams,
                batchSize
              );

              loadedBriefs.push(...pageBriefs);
            } catch (error) {
              this.$error(`Error loading batch ${page}:`, error);
            }
          }
        }

        this.loadingProgress = 1;

        this.briefs = loadedBriefs.map((brief) =>
          Object.assign(brief, { users: brief.users || [] })
        );
      } catch (error) {
        this.$error("Something went wrong loading briefs", error);
      } finally {
        this.loadingBriefs = false;
      }
    },
    async loadUsers() {
      try {
        this.loadingUsers = true;

        const currentFilters = {
          organisationId: this.organisationId,
          active: true,
        };

        Object.keys(this.userFilters).forEach((key) => {
          const filter = {};
          filter[this.userFilters[key].operator] = this.userFilters[key].value;
          currentFilters[key] = filter;
        });

        const response = await User.where(currentFilters)
          .per(this.usersPagination.rowsPerPage)
          .page(this.usersPagination.page)
          .select({
            users: ["firstName", "lastName", "email", "briefSetIds"],
          })
          .selectExtra({ users: ["teamName"] })
          .stats({ total: "count" })
          .all();

        this.users = response.data;
        this.usersPagination.rowsNumber = response.meta.stats.total.count;
      } catch (error) {
        this.$error("Something went wrong loading users", error);
      } finally {
        this.loadingUsers = false;
      }
    },
    async bulkAssignBriefSetsUsers(userIds) {
      try {
        await this.$streemApiAdmin.put("brief_sets/bulk_apply", {
          params: {
            user_ids: userIds,
            brief_set_ids: this.selectedBriefSetIds,
          },
        });
      } catch (error) {
        this.$error(
          "There was an error bulk assigning brief set to users",
          error
        );
      }
    },
    async bulkUnassignBriefSetsFromUsers(userIds) {
      try {
        await this.$streemApiAdmin.put("brief_sets/bulk_unassign", {
          params: {
            user_ids: userIds,
            brief_set_ids: this.selectedBriefSetIds,
          },
        });
      } catch (error) {
        this.$error(
          "There was an error bulk unassigning brief set to users",
          error
        );
      }
    },
    async bulkAssignBriefsToUsers(briefIds) {
      const userIds = this.selectedUsers.map((user) => Number(user.id));

      try {
        await this.$streemApiAdmin.put("briefs/bulk_assign_users", {
          params: {
            user_ids: userIds,
            brief_ids: briefIds,
          },
        });
      } catch (error) {
        this.$error("There was an error bulk assigning briefs to users", error);
      }
    },

    async bulkUnassignBriefsFromUsers(briefIds) {
      const userIds = this.selectedUsers.map((user) => Number(user.id));

      try {
        await this.$streemApiAdmin.put("briefs/bulk_unassign_users", {
          params: {
            user_ids: userIds,
            brief_ids: briefIds,
          },
        });
      } catch (error) {
        this.$error(
          "There was an error bulk unassigning briefs from users",
          error
        );
      }
    },
    async save() {
      const userIds = this.selectedUsers.map((user) => Number(user.id));

      if (
        this.briefSelectionMethod === "assign_brief_sets" &&
        this.selectedBriefSetIds
      ) {
        await this.bulkAssignBriefSetsUsers(userIds);

        this.$emit("close");

        return;
      }

      if (
        this.briefSelectionMethod === "assign_individual_briefs" &&
        this.selectedBriefs
      ) {
        const briefIds = this.selectedBriefs.map((brief) => Number(brief.id));
        await this.bulkAssignBriefsToUsers(briefIds);

        this.$emit("refresh");

        this.$emit("close");

        return;
      }

      if (
        this.briefSelectionMethod === "unassign_individual_briefs" &&
        this.selectedBriefs
      ) {
        const briefIds = this.selectedBriefs.map((brief) => Number(brief.id));
        await this.bulkUnassignBriefsFromUsers(briefIds);

        this.$emit("close");

        return;
      }

      if (
        this.briefSelectionMethod === "unassign_brief_sets" &&
        this.selectedBriefSetIds
      ) {
        await this.bulkUnassignBriefSetsFromUsers(userIds);

        this.$emit("close");

        return;
      }

      if (this.briefSelectionMethod === "unassign_all_briefs_and_brief_sets") {
        await this.bulkUnassignAllBriefsAndBriefSets(userIds);

        this.$emit("close");
      }
    },
    async bulkUnassignAllBriefsAndBriefSets(userIds) {
      try {
        await this.$streemApiAdmin.put("users/bulk_unassign_all_briefs", {
          params: {
            user_ids: userIds,
            organisation_id: this.organisationId,
          },
        });
      } catch (error) {
        this.$error(
          "There was an error bulk unassigning all briefs and brief sets for users",
          error
        );
      }
    },
    async loadBriefSetsStreams() {
      const response = await BriefSet.includes(BriefSetsStream.jsonapiType)
        .where({ id: this.selectedBriefSetIds })
        .all();

      return response.data;
    },
  },
};
</script>

<style lang="scss" scoped>
.c-enable-briefs-details-step {
  &__section {
    padding-bottom: 20px;
    width: 100%;
  }

  &__section-header {
    font-weight: 500;
    font-size: 20px;
  }

  &__section-subheader {
    font-weight: 400;
    font-size: 13px;
  }
}

:deep(.streem-modal) {
  .container {
    background-color: white;
  }
}
</style>
